import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import { CreateWishlistModalContent } from './CreateWishlistModal'
import WishlistPickerList from './WishlistPickerList'
import type { WishlistSavedState, WishlistBookmarkPayload } from '../types'
import { useWishlistIndex } from '../hooks'
import useSaveToWishlistFlow from '../hooks/useSaveToWishlistFlow'

const Section = styled.div`
  display: grid;
  gap: ${rem(16)};
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
`

interface Props {
  item: WishlistBookmarkPayload
  title: string
  imageIds: Array<string>
  subtitle?: string
  imageId?: string
  deeplinkUrl?: string
  locationName?: string
  suggestedWishlistName?: string
  latitude?: number
  longitude?: number
  initialSavedState?: WishlistSavedState
}

function SaveToWishlistModal({
  item,
  title,
  imageIds,
  subtitle,
  imageId,
  deeplinkUrl,
  locationName,
  suggestedWishlistName,
  latitude,
  longitude,
  initialSavedState,
}: Props) {
  const intl = useIntl()
  const { data, isFetching, isError } = useWishlistIndex()
  const [view, setView] = useState<'select' | 'create'>('select')

  const customWishlists = useMemo(
    () => data?.wishlists.filter((wishlist) => !wishlist.isDefault) ?? [],
    [data],
  )
  const isInitialising = isFetching
  const hasWishlistData = Boolean(data)
  const shouldSkipWishlistPicker =
    hasWishlistData && !isInitialising && !isError && customWishlists.length === 0
  const isCreateView = view === 'create' || shouldSkipWishlistPicker
  const secondaryActionLabel = shouldSkipWishlistPicker
    ? undefined
    : intl.formatMessage({
        id: 'MrkbHzkQIay8LzUDoq-0',
        defaultMessage: 'Back',
        description: 'Secondary action label that returns from create wishlist to the picker view',
      })
  const onSecondaryAction = shouldSkipWishlistPicker
    ? undefined
    : () => setView('select')
  const {
    canSaveToWishlist,
    createAndSave,
    isCreatingWishlist,
    isMutating,
    nameError,
    newWishlistName,
    onDismiss,
    saveToWishlist,
    setNameError,
    setNewWishlistName,
  } = useSaveToWishlistFlow({
    item,
    title,
    imageIds,
    subtitle,
    imageId,
    deeplinkUrl,
    locationName,
    suggestedWishlistName,
    latitude,
    longitude,
    initialSavedState,
    customWishlists,
  })
  const wishlistSection = (
    <Section>
      <SectionHeader>
        <Heading variant="heading5">
          {intl.formatMessage({
            id: 'JZO-7bNsBt4DpVO718c0',
            defaultMessage: 'Wishlists',
            description: 'Section heading shown above the wishlist picker in the save modal',
          })}
        </Heading>
        <TextButton
          kind="tertiary"
          size="medium"
          fit="auto"
          type="button"
          disabled={!canSaveToWishlist}
          onClick={() => setView('create')}
        >
          {intl.formatMessage({
            id: '72UWaygBEzXRlw9FEh7D',
            defaultMessage: 'New Wishlist',
            description: 'Button label that opens the create wishlist view from the save modal',
          })}
        </TextButton>
      </SectionHeader>

      {!canSaveToWishlist && (
        <BodyText variant="medium" colour="light-foreground-alt">
          {intl.formatMessage({
            id: '1-FTlLutwi3IxRMeYZva',
            defaultMessage: 'We could not determine your region right now. Please refresh and try again.',
            description: 'Error message shown when the save to wishlist modal cannot determine a valid region for saving',
          })}
        </BodyText>
      )}

      {canSaveToWishlist && (isError || !data) && (
        <BodyText variant="medium" colour="light-foreground-alt">
          {intl.formatMessage({
            id: 'ZmvbTfRnxJ22Bu51frG4',
            defaultMessage: 'We could not load your wishlists right now. Please try again.',
            description: 'Error message shown when the save modal fails to load the customer wishlists',
          })}
        </BodyText>
      )}

      {canSaveToWishlist && !isError && data && customWishlists.length > 0 && (
        <WishlistPickerList
          wishlists={customWishlists}
          isDisabled={isMutating || !canSaveToWishlist}
          onSelect={(wishlistId) => {
            saveToWishlist(wishlistId).catch(() => undefined)
          }}
        />
      )}
    </Section>
  )
  const modalContent = isCreateView ? (
    <CreateWishlistModalContent
      value={newWishlistName}
      error={nameError}
      isSubmitting={isCreatingWishlist}
      secondaryActionLabel={secondaryActionLabel}
      onChange={(value) => {
        setNewWishlistName(value)
        if (nameError) {
          setNameError('')
        }
      }}
      onSubmit={createAndSave}
      onSecondaryAction={onSecondaryAction}
      onClose={onDismiss}
    />
  ) : (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: '1RXAszdNZTt-hKbIYJPH',
          defaultMessage: 'Save to Wishlist',
          description: 'Wishlist modal heading when the offer has not yet been saved to a wishlist',
        })}
        onCloseButtonClick={onDismiss}
      />
      <ModalBody>
        <ModalContent>
          {isInitialising ? <LoadingIndicator floating /> : wishlistSection}
        </ModalContent>
      </ModalBody>
    </>
  )

  return (
    <ModalBase size="S">
      {modalContent}
    </ModalBase>
  )
}

export default SaveToWishlistModal
